"use client";

import * as React from "react";
import * as CommandPrimitive from "cmdk";
import { SearchIcon } from '@/shared/ui/icons';

import { cn } from "./utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./dialog";

const CommandModule = CommandPrimitive as any;
const RadixCommand = Object.assign(
  {},
  CommandModule,
  typeof CommandModule?.default === "object" ? CommandModule.default : {},
);
RadixCommand.Command = CommandModule?.Command ?? CommandModule?.default ?? CommandModule;
RadixCommand.Input =
  CommandModule?.Input ?? CommandModule?.default?.Input ?? RadixCommand.Command?.Input;
RadixCommand.List =
  CommandModule?.List ?? CommandModule?.default?.List ?? RadixCommand.Command?.List;
RadixCommand.Empty =
  CommandModule?.Empty ?? CommandModule?.default?.Empty ?? RadixCommand.Command?.Empty;
RadixCommand.Group =
  CommandModule?.Group ?? CommandModule?.default?.Group ?? RadixCommand.Command?.Group;
RadixCommand.Separator =
  CommandModule?.Separator ??
  CommandModule?.default?.Separator ??
  RadixCommand.Command?.Separator;
RadixCommand.Item =
  CommandModule?.Item ?? CommandModule?.default?.Item ?? RadixCommand.Command?.Item;

const CommandRoot = RadixCommand.Command;

const hasCmdk =
  typeof CommandRoot !== "undefined" &&
  typeof RadixCommand.Input !== "undefined" &&
  typeof RadixCommand.List !== "undefined" &&
  typeof RadixCommand.Empty !== "undefined" &&
  typeof RadixCommand.Group !== "undefined" &&
  typeof RadixCommand.Separator !== "undefined" &&
  typeof RadixCommand.Item !== "undefined";

let hasWarnedMissingCmdk = false;
const warnMissingCmdk = () => {
  if (hasWarnedMissingCmdk) return;
  hasWarnedMissingCmdk = true;
  console.warn("[ui/command] cmdk is unavailable. Falling back to basic markup.");
};

function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandRoot>) {
  if (!hasCmdk) {
    warnMissingCmdk();
    return (
      <div
        data-slot="command-fallback"
        className={cn(
          "bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden [border-radius:var(--ui-popover-radius)] [font-family:var(--ui-font)]",
          className,
        )}
        {...(props as any)}
      />
    );
  }
  return (
    <CommandRoot
      data-slot="command"
      className={cn(
        "bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden [border-radius:var(--ui-popover-radius)] [font-family:var(--ui-font)]",
        className,
      )}
      {...props}
    />
  );
}

function CommandDialog({
  title = "Command Palette",
  description = "Search for a command to run...",
  children,
  ...props
}: React.ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
}) {
  if (!hasCmdk) {
    warnMissingCmdk();
    return (
      <Dialog {...props}>
        <DialogContent className="overflow-hidden p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12">
            {children}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Command className="[&_[cmdk-group-heading]]:text-muted-foreground **:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof RadixCommand.Input>) {
  const generatedId = React.useId();
  if (!hasCmdk) {
    warnMissingCmdk();
    return (
      <div
        data-slot="command-input-wrapper-fallback"
        className="flex h-9 items-center gap-2 border-b px-3"
      >
        <SearchIcon className="size-4 shrink-0 opacity-50" />
        <input
          id={(props as any).id ?? (props as any).name ?? generatedId}
          data-slot="command-input-fallback"
          className={cn(
            "placeholder:text-muted-foreground flex h-10 w-full [border-radius:var(--ui-control-radius)] bg-transparent py-3 text-sm [font-family:var(--ui-font)] outline-hidden",
            className,
          )}
          {...(props as any)}
        />
      </div>
    );
  }
  return (
    <div
      data-slot="command-input-wrapper"
      className="flex h-9 items-center gap-2 border-b px-3"
    >
      <SearchIcon className="size-4 shrink-0 opacity-50" />
      <RadixCommand.Input
        id={(props as any).id ?? (props as any).name ?? generatedId}
        data-slot="command-input"
        className={cn(
          "placeholder:text-muted-foreground flex h-10 w-full [border-radius:var(--ui-control-radius)] bg-transparent py-3 text-sm [font-family:var(--ui-font)] outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    </div>
  );
}

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof RadixCommand.List>) {
  if (!hasCmdk) {
    warnMissingCmdk();
    return (
      <div
        data-slot="command-list-fallback"
        className={cn("max-h-[300px] overflow-y-auto", className)}
        {...(props as any)}
      />
    );
  }
  return (
    <RadixCommand.List
      data-slot="command-list"
      className={cn(
        "max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto",
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  ...props
}: React.ComponentProps<typeof RadixCommand.Empty>) {
  if (!hasCmdk) {
    warnMissingCmdk();
    return (
      <div data-slot="command-empty-fallback" className="py-6 text-center text-sm" {...(props as any)} />
    );
  }
  return (
    <RadixCommand.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadixCommand.Group>) {
  if (!hasCmdk) {
    warnMissingCmdk();
    return (
      <div
        data-slot="command-group-fallback"
        className={cn("text-foreground overflow-hidden p-1", className)}
        {...(props as any)}
      />
    );
  }
  return (
    <RadixCommand.Group
      data-slot="command-group"
      className={cn(
        "text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium",
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof RadixCommand.Separator>) {
  if (!hasCmdk) {
    warnMissingCmdk();
    return (
      <div
        data-slot="command-separator-fallback"
        className={cn("bg-border -mx-1 h-px", className)}
        {...(props as any)}
      />
    );
  }
  return (
    <RadixCommand.Separator
      data-slot="command-separator"
      className={cn("bg-border -mx-1 h-px", className)}
      {...props}
    />
  );
}

function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof RadixCommand.Item>) {
  if (!hasCmdk) {
    warnMissingCmdk();
    return (
      <div
        data-slot="command-item-fallback"
        className={cn(
          "relative flex cursor-default items-center gap-2 [border-radius:var(--ui-menu-item-radius)] px-2 py-1.5 text-sm [font-family:var(--ui-font)]",
          className,
        )}
        {...(props as any)}
      />
    );
  }
  return (
    <RadixCommand.Item
      data-slot="command-item"
      className={cn(
        "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 [border-radius:var(--ui-menu-item-radius)] px-2 py-1.5 text-sm [font-family:var(--ui-font)] outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        "text-muted-foreground ml-auto text-xs tracking-widest",
        className,
      )}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
